import React, { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { uploadAvatar } from '../api/account';

/**
 * AvatarRequiredGate — Phase F5.2 product rule: NEW users = username + photo.
 * Blocking overlay shown ONLY to accounts created with avatar_prompt_required
 * (registrations after F5.2) that still have no avatar. Legacy users and
 * OAuth users with a provider picture are never gated. Login/session intact.
 */
const AvatarRequiredGate = () => {
  const { user, refresh, logout } = useAuth();
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  if (!user || !user.email_verified || !user.avatar_prompt_required || user.avatar_url) return null;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      await uploadAvatar(file);
      await refresh();
      toast({ title: 'Foto profilo aggiunta!' });
    } catch (_err) {
      setPreview(null);
      toast({ title: 'Upload non riuscito', description: 'Riprova con un altro file.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm" data-testid="avatar-required-gate">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-amber-400/60 bg-secondary">
          {preview
            ? <img src={preview} alt="anteprima" className="h-full w-full object-cover object-center" />
            : <Camera className="h-8 w-8 gold-text" strokeWidth={1.5} />}
        </div>
        <h2 className="text-lg font-bold">Aggiungi la tua foto profilo</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Su Nymia la tua identità è <span className="font-semibold">@{user.username}</span> + la tua foto.
          Carica una foto per iniziare.
        </p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="avatar-gate-input" />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="gold-gradient mt-5 w-full font-bold text-black"
          data-testid="avatar-gate-upload-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Carica foto'}
        </Button>
        <button
          onClick={logout}
          className="mt-3 text-xs text-muted-foreground underline-offset-2 hover:underline"
          data-testid="avatar-gate-logout-btn"
        >
          Esci
        </button>
      </div>
    </div>
  );
};

export default AvatarRequiredGate;
